/**
 * Compact age labels for dense lists — "2 d", "5 h", "1 w".
 *
 * Deliberately coarse: the home list wants a glanceable sense of freshness
 * in one column, not a precise timestamp. Detail views keep rendering full
 * dates via `toLocaleDateString`.
 */

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY
const YEAR = 365 * DAY

/**
 * Returns the age of `iso` as a short label. Unparseable input and future
 * timestamps (clock skew between the DB and the browser) both collapse to
 * "now" rather than rendering "NaN d" or a negative age.
 *
 * `now` is injectable so callers — and tests — control the reference point.
 */
export function formatCompactAge(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return 'now'

  const elapsed = now - then
  if (elapsed < MINUTE) return 'now'
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} m`
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)} h`
  if (elapsed < WEEK) return `${Math.floor(elapsed / DAY)} d`
  if (elapsed < YEAR) return `${Math.floor(elapsed / WEEK)} w`
  return `${Math.floor(elapsed / YEAR)} y`
}
